import React, { useEffect } from 'react';

import { ChakraProvider } from '@chakra-ui/react';
import truncate from 'lodash/truncate';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';

import { ErrorBoundary } from '@r-client/republic/feature/errors';
import {
  EventDetailsContent,
  GetEventDetailsQuery,
  SEO_DESCRIPTION_LENGTH,
  useGetEventDetailsQuery,
} from '@r-client/republic/feature/events';
import { MainLayout } from '@r-client/republic/feature/main-layout';
import { defaultCoverImage, PageSEOMeta } from '@r-client/republic/feature/seo';
import { republicLegacy } from '@r-client/republic/ui/theme';
import { useAnalytics } from '@r-client/shared/data/analytics';
import {
  GqlClientStateProp,
  withGqlClientState,
} from '@r-client/shared/data/client';
import { BackLink, Box, Container } from '@r-client/shared/ui/core';
import { toAbsoluteURL, useAppConfig } from '@r-client/shared/util/core';

import { createGqlClient } from '../../../gql-client';

import styles from './index.module.scss';

export const getServerSideProps: GetServerSideProps<
  GqlClientStateProp & { slug: string }
> = async ({ res, params }) => {
  const slug = params?.slug;

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=60, stale-while-revalidate=120'
  );

  if (!slug || Array.isArray(slug)) {
    return {
      notFound: true,
    };
  }

  const gqlClient = createGqlClient();
  const eventQuery = await new GetEventDetailsQuery({
    variables: { slug },
    lazy: true,
    client: gqlClient,
  }).load();

  if (!eventQuery?.data?.event) {
    return {
      notFound: true,
    };
  }

  return {
    props: withGqlClientState(gqlClient, { slug }),
  };
};

type IEventDetailsProps = InferGetServerSidePropsType<
  typeof getServerSideProps
>;

export const EventDetails = observer(function EventDetails({
  slug,
}: IEventDetailsProps) {
  const analytics = useAnalytics();
  const router = useRouter();
  const { applicationURLOrigin } = useAppConfig();

  const eventQuery = useGetEventDetailsQuery({
    variables: { slug },
  });

  useEffect(() => {
    if (slug) analytics.page({ category: 'events', name: slug });
  }, [slug, analytics]);

  const seoTitle = eventQuery.data?.event?.title;
  const seoDescription = truncate(eventQuery.data?.event?.bodySanitized ?? '', {
    length: SEO_DESCRIPTION_LENGTH,
  });
  const seoImage =
    eventQuery.data?.event?.coverPhotoUrl ??
    toAbsoluteURL(applicationURLOrigin, defaultCoverImage);
  return (
    <>
      <PageSEOMeta
        title={seoTitle}
        metaTitle={seoTitle}
        description={seoDescription}
        url={toAbsoluteURL(applicationURLOrigin, router.asPath)}
        image={seoImage}
      />
      <ChakraProvider theme={republicLegacy}>
        <MainLayout>
          <ErrorBoundary>
            <Container pageContent className={styles.container}>
              <Box m={Box.bottom(4)}>
                <Box m={Box.bottom(1)}>
                  <BackLink href="/events" label="Events" />
                </Box>
                <EventDetailsContent eventQuery={eventQuery} />
              </Box>
            </Container>
          </ErrorBoundary>
        </MainLayout>
      </ChakraProvider>
    </>
  );
});

export default EventDetails;
